<script setup lang="ts">
import { computed } from 'vue';
import PlsButton1 from '@/components/PlsButton1.vue';
import MdInfo from '@/components/icons/MdInfo.vue';
import { analytics, syncLogs } from '@/common/logs';
import { BTN_EVTS } from '@/directives/trackClick';
import { platform } from '@/common/bridge';
import { useArticleAction } from '../composables/useArticleAction';
import { useArticleActionToBeTakenButtonCustomisation } from '../composables/useArticleButtonCustomisation';

interface Props {
  articleId: number;
  canConnectToSupport: boolean;
}

const props = defineProps<Props>();

const { canShowAction, articleAction, goToArticleAction } = useArticleAction({
  articleId: computed(() => props.articleId),
});

const handleArticleActionClick = async () => {
  analytics.trackClick({
    name: BTN_EVTS.ARTICLE_INTERACT_ACTION,
    selection: props.articleId,
  });

  const isAppPlatform = platform === 'ios' || platform === 'app';
  await syncLogs({ endSession: !isAppPlatform });
  goToArticleAction();
};

// Customisation
const buttonCustomisation = useArticleActionToBeTakenButtonCustomisation();

const { buttonTextColor, buttonBackgroundColor, buttonBorderColor } = buttonCustomisation;
</script>

<template>
  <div v-if="canShowAction.box" class="article-actions mt-1">
    <div v-if="canShowAction.warning" class="align-center article-check-container d-flex justify-center p-2">
      <MdInfo class="check-icon mr-1" />
      <span>{{ canShowAction.warningMessage }}</span>
    </div>

    <div v-if="canShowAction.button && articleAction" class="action-button-wrapper align-center d-flex mt-1">
      <PlsButton1
        :text="articleAction.actionTitle || ''"
        :text-color="buttonTextColor"
        :background-color="buttonBackgroundColor"
        :border-color="buttonBorderColor"
        class="action-button"
        @click="handleArticleActionClick"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';

.article-actions {
  .action-button-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;

    .action-button {
      flex: 1;
      font-size: $size-m;
      min-width: 0;
    }

    .action-icon {
      font-size: $size-m;
      flex-shrink: 0;
    }
  }
}

.article-check-container {
  background-color: $article-check-container-background;
  line-height: 1.2;
  font-size: $size-m2;

  .check-icon {
    font-size: $size-xl;
    color: $article-check-container-icon-color;
  }
}

// Utility classes matching old Article component
.mt-1 {
  margin-top: 8px;
}

.d-flex {
  display: flex;
}

.justify-center {
  justify-content: center;
}

.align-center {
  align-items: center;
}

.p-2 {
  padding: 16px;
}

.mr-1 {
  margin-right: 8px;
}

.ml-1 {
  margin-left: 8px;
}
</style>
